import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, PartyPopper, Loader2 } from "lucide-react";
import ChapterHeading from "./ChapterHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  attending: true,
  guests_count: 1,
  guest_names: [""],
  kids_info: "",
  menu_choice: "Classico",
  dietary_requirements: "",
  song_request: "",
  notes: "",
};

export default function Rsvp() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setGuests = (value) => {
    if (value === "") {
      setForm((f) => ({ ...f, guests_count: "" }));
      return;
    }
    const n = Math.min(20, Math.max(1, Number(value) || 1));
    setForm((f) => ({
      ...f,
      guests_count: n,
      guest_names: Array.from({ length: n }, (_, i) => f.guest_names[i] || ""),
    }));
  };

  const setGuestName = (i, value) =>
    setForm((f) => ({
      ...f,
      guest_names: f.guest_names.map((g, idx) => (idx === i ? value : g)),
    }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const payload = {
        ...form,
        guests_count: Math.min(20, Math.max(1, Number(form.guests_count) || form.guest_names.length || 1)),
        name: form.guest_names[0],
      };
      if (!form.attending) {
        payload.menu_choice = "";
        payload.kids_info = "";
        payload.dietary_requirements = "";
        payload.song_request = "";
        payload.notes = "";
      }
      await axios.post(`${API}/rsvp`, payload);
      setStatus("done");
      toast.success("Presenza registrata. Grazie, ti aspettiamo a bordo!");
    } catch {
      setStatus("idle");
      toast.error("Qualcosa è andato storto. Riprova tra poco.");
    }
  };

  return (
    <section id="rsvp" className="px-6 sm:px-12 lg:px-24 py-20 sm:py-28" data-testid="rsvp-section">
      <div className="mx-auto max-w-4xl">
        <ChapterHeading
          number="05"
          title="Conferma la Tua Presenza"
          kicker="Vi chiediamo gentilmente di confermare entro il 15 Aprile 2027. Il check-in è aperto: compilate la carta d'imbarco qui sotto."
          testid="rsvp-heading"
        />

        {status === "done" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-sand bg-cream p-10 sm:p-14 text-center"
            data-testid="rsvp-success"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-panna border border-sand flex items-center justify-center mb-6">
              <PartyPopper className="w-7 h-7 text-terracotta" />
            </div>
            <h3 className="font-serif text-3xl text-[#2D2A26] mb-4">Imbarco confermato!</h3>
            <p className="text-[#5C564E] leading-relaxed max-w-md mx-auto">
              Grazie {form.guest_names[0].split(" ")[0]}, la tua risposta è stata registrata. Non vediamo
              l'ora di festeggiare insieme a Villa Grazioli.
            </p>
            <button
              onClick={() => { setForm(initialForm); setStatus("idle"); }}
              className="mt-8 rounded-full border border-charcoal/20 bg-panna px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[#2D2A26] transition-all duration-300 hover:bg-charcoal hover:text-panna"
              data-testid="rsvp-another-button"
            >
              Aggiungi un'altra risposta
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={submit}
            className="rounded-2xl border border-sand bg-cream p-8 sm:p-12"
            data-testid="rsvp-form"
          >
            <div className="space-y-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Sarai dei nostri?</Label>
              <RadioGroup value={form.attending ? "si" : "no"} onValueChange={(v) => set("attending", v === "si")} className="flex gap-4 pt-2" data-testid="rsvp-attending-group">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="si" id="att-si" data-testid="rsvp-attending-yes" />
                  <Label htmlFor="att-si" className="text-sm text-[#2D2A26]">Sì, ci sarò</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="no" id="att-no" data-testid="rsvp-attending-no" />
                  <Label htmlFor="att-no" className="text-sm text-[#2D2A26]">Purtroppo no</Label>
                </div>
              </RadioGroup>
              {!form.attending && (
                <motion.p
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-3 rounded-xl border border-sand bg-panna px-4 py-3 text-sm leading-relaxed text-[#5C564E]"
                  data-testid="rsvp-not-attending-note"
                >
                  Ci dispiace tanto non poter festeggiare insieme, ti chiediamo
                  comunque di compilare le seguenti sezioni e di inviarci le
                  risposte, grazie.
                </motion.p>
              )}
            </div>

            <div className="mt-8 max-w-xs space-y-2">
              <Label htmlFor="rsvp-guests" className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Numero di invitati</Label>
              <Input id="rsvp-guests" type="number" required min="1" max="20" value={form.guests_count} onChange={(e) => setGuests(e.target.value)} className="bg-panna border-sand" data-testid="rsvp-guests-input" />
            </div>

            <div className="mt-8 space-y-3">
              <Label className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Nome e Cognome</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {form.guest_names.map((g, i) => (
                  <Input
                    key={i}
                    required
                    value={g}
                    onChange={(e) => setGuestName(i, e.target.value)}
                    placeholder={`Invitato ${i + 1} — Nome e Cognome`}
                    className="bg-panna border-sand"
                    data-testid={`rsvp-guest-name-${i}`}
                  />
                ))}
              </div>
            </div>

            {form.attending && (
              <>
            <div className="mt-8 space-y-2">
              <Label htmlFor="rsvp-kids" className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Bimbi al seguito?</Label>
              <Input id="rsvp-kids" value={form.kids_info} onChange={(e) => set("kids_info", e.target.value)} placeholder="Es. 2 bimbi, 4 e 7 anni" className="bg-panna border-sand" data-testid="rsvp-kids-input" />
            </div>

            <div className="mt-8 space-y-3">
              <Label className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Scelta del menù</Label>
              <RadioGroup value={form.menu_choice} onValueChange={(v) => set("menu_choice", v)} className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="rsvp-menu-group">
                {["Classico", "Vegetariano", "Vegano"].map((m) => (
                  <label
                    key={m}
                    htmlFor={`menu-${m}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 cursor-pointer transition-all duration-300 ${form.menu_choice === m ? "border-terracotta bg-panna shadow-sm" : "border-sand bg-panna/60 hover:bg-panna"}`}
                    data-testid={`rsvp-menu-${m.toLowerCase()}`}
                  >
                    <RadioGroupItem value={m} id={`menu-${m}`} />
                    <span className="text-sm text-[#2D2A26]">{m}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <Label htmlFor="rsvp-dietary" className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Intolleranze alimentari o allergie</Label>
                <Textarea id="rsvp-dietary" value={form.dietary_requirements} onChange={(e) => set("dietary_requirements", e.target.value)} placeholder="Es. celiachia, intolleranza al lattosio, no frutta a guscio..." className="bg-panna border-sand min-h-[90px]" data-testid="rsvp-dietary-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rsvp-song" className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Una canzone che ti farebbe ballare</Label>
                <Input id="rsvp-song" value={form.song_request} onChange={(e) => set("song_request", e.target.value)} placeholder="Titolo e artista" className="bg-panna border-sand" data-testid="rsvp-song-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rsvp-notes" className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Note o messaggio per gli sposi</Label>
                <Textarea id="rsvp-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Scrivici qualcosa..." className="bg-panna border-sand min-h-[90px]" data-testid="rsvp-notes-input" />
              </div>
            </div>
              </>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-terracotta text-panna font-mono text-[11px] uppercase tracking-[0.25em] px-10 py-4 transition-all duration-300 hover:bg-[#b25c42] hover:gap-5 disabled:opacity-60"
              data-testid="rsvp-submit-button"
            >
              {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {status === "loading" ? "Invio in corso..." : "Invia conferma"}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
}
